import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetAllPersonsQuery, useLazyGetPersonByIdQuery } from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { handleFullName, isSubstringOf } from "@/shared/utils";
import {
  MENU_SORTER_OPTIONS,
  type MenuSorterOptions,
} from "@/common/AppHeader/mobile/Components/Menu/MenuSorterList";
import type { MaybeType } from "@/shared/types";
import type { PersonGroup, TableData } from "../types";
import {
  currentPersonSelector,
  groupFilterSelector,
  nameFilterSelector,
  setGroupFilter,
  setNameFilter,
  setTitleFilter,
  sorterSelector,
  setSorter,
  titleFilterSelector,
  swipedPersonSelector,
  setSwipedPerson,
  setCurrentPerson,
  setContactsIsEdit,
} from "../slice";
import { getAllNameCombinations } from "../helpers";

export const useContactsPageDesktop = () => {
  const dispatch = useAppDispatch();

  const groupFilter = useAppSelector(groupFilterSelector);
  const nameFilter = useAppSelector(nameFilterSelector);
  const titleFilter = useAppSelector(titleFilterSelector);
  const sorter = useAppSelector(sorterSelector);
  const swipedPerson = useAppSelector(swipedPersonSelector);

  const paramsPersonId = useParams().personId;

  const { data, isLoading, isError, isFetching } = useGetAllPersonsQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();

  const getPerson = (personId: string) => {
    getPersonById({ id: personId })
      .unwrap()
      .then((res) => {
        dispatch(setCurrentPerson(res));
      });
  };

  useEffect(() => {
    if (paramsPersonId) {
      dispatch(setContactsIsEdit(false));

      getPerson(paramsPersonId);
    }
  }, [isFetching, paramsPersonId]);

  const currentPerson = useAppSelector(currentPersonSelector);

  const setMenuSorter = (sort: MenuSorterOptions) => {
    dispatch(setSorter(sort));
  };

  const handleChangeGroupFilter = useCallback(
    (group: PersonGroup) => {
      dispatch(setGroupFilter(group));
    },
    [dispatch],
  );

  const handleChangeNameFilter = useCallback(
    (value: MaybeType<string>) => {
      dispatch(setNameFilter(value));
    },
    [dispatch],
  );

  const handleChangeTitleFilter = useCallback(
    (value: MaybeType<string>) => {
      dispatch(setTitleFilter(value));
    },
    [dispatch],
  );

  const handleSwipedPerson = useCallback(
    (personId: MaybeType<string>) => {
      dispatch(setSwipedPerson(personId));
    },
    [dispatch],
  );

  let filteredContacts: TableData = useMemo(() => {
    const filtered =
      data?.persons.filter((person) => {
        const allPersonNameCombination = getAllNameCombinations({
          firstName: person.firstName ?? "",
          lastName: person.lastName ?? "",
          middleName: person.middleName ?? "",
        });

        const matchesGroup = !groupFilter || person.group === groupFilter;
        const matchesName = !!allPersonNameCombination.filter((fullName) => {
          return isSubstringOf({ sourceString: fullName, searchString: nameFilter ?? "" });
        }).length;

        const matchesTitle =
          !titleFilter ||
          isSubstringOf({ sourceString: person?.title ?? "", searchString: titleFilter });

        return matchesGroup && matchesName && matchesTitle;
      }) ?? [];

    return filtered.map((person) => ({
      key: person.id,
      group: person.group ?? "",
      avatar: person?.avatarFile?.fileName,
      name: handleFullName(person?.firstName, person?.lastName),
      title: person.title,
      person: person,
    }));
  }, [data, groupFilter, nameFilter, titleFilter, sorter]);

  if (sorter) {
    switch (sorter) {
      case MENU_SORTER_OPTIONS.NAME_ASC:
        filteredContacts = filteredContacts.sort((a, b) => {
          const aSort = a.person?.firstName ?? "";
          const bSort = b.person?.firstName ?? "";
          if (aSort < bSort) {
            return -1;
          }
          if (aSort > bSort) {
            return 1;
          }
          return 0;
        });
        break;
      case MENU_SORTER_OPTIONS.NAME_DESC:
        filteredContacts = filteredContacts
          .sort((a, b) => {
            const aSort = a.person?.firstName ?? "";
            const bSort = b.person?.firstName ?? "";
            if (aSort < bSort) {
              return -1;
            }
            if (aSort > bSort) {
              return 1;
            }
            return 0;
          })
          .reverse();
        break;
      case MENU_SORTER_OPTIONS.SECOND_NAME_ASC:
        filteredContacts = filteredContacts.sort((a, b) => {
          const aSort = a.person?.lastName ?? "";
          const bSort = b.person?.lastName ?? "";
          if (aSort < bSort) {
            return -1;
          }
          if (aSort > bSort) {
            return 1;
          }
          return 0;
        });
        break;
      case MENU_SORTER_OPTIONS.SECOND_NAME_DESC:
        filteredContacts = filteredContacts
          .sort((a, b) => {
            const aSort = a.person?.lastName ?? "";
            const bSort = b.person?.lastName ?? "";
            if (aSort < bSort) {
              return -1;
            }
            if (aSort > bSort) {
              return 1;
            }
            return 0;
          })
          .reverse();
        break;
      case MENU_SORTER_OPTIONS.TITLE_ASC:
        filteredContacts = filteredContacts.sort((a, b) => {
          const aSort = a.person?.title ?? "";
          const bSort = b.person?.title ?? "";
          if (aSort < bSort) {
            return -1;
          }
          if (aSort > bSort) {
            return 1;
          }
          return 0;
        });
        break;
      case MENU_SORTER_OPTIONS.TITLE_DESC:
        filteredContacts = filteredContacts
          .sort((a, b) => {
            const aSort = a.person?.title ?? "";
            const bSort = b.person?.title ?? "";
            if (aSort < bSort) {
              return -1;
            }
            if (aSort > bSort) {
              return 1;
            }
            return 0;
          })
          .reverse();
        break;
    }
  }

  const isEmpty = !data || (!data.persons.length && !currentPerson);

  return {
    state: {
      filteredContacts,
      isLoading,
      isError,
      isEmpty,
      titleFilter,
      nameFilter,
      sorter,
      swipedPerson,
    },
    methods: {
      setMenuSorter,
      handleChangeGroupFilter,
      handleChangeNameFilter,
      handleChangeTitleFilter,
      handleSwipedPerson,
    },
  };
};
